/**
 * Utilities for ZIP Code (CEP) formatting and validation
 */

/**
 * Formats a ZIP Code applying the mask XXXXX-XXX
 * @param value - ZIP Code value without formatting
 * @returns Formatted ZIP Code
 */
export const formatZipCode = (value: string | number | null | undefined): string => {
  // Handle null, undefined, or non-string values
  if (value === null || value === undefined) {
    return '';
  }

  // Convert to string if it's a number
  const stringValue = typeof value === 'string' ? value : String(value);

  // Remove everything that is not a digit
  const numbers = stringValue.replace(/\D/g, '');

  // Apply ZIP Code mask XXXXX-XXX
  return numbers
    .replace(/(\d{5})(\d)/, '$1-$2')
    .slice(0, 9); // Limit to 9 characters (5 digits + 1 hyphen + 3 digits)
};

/**
 * Validates if a ZIP Code is valid (8 digits)
 * @param zipCode - ZIP Code with or without formatting
 * @returns true if the ZIP Code is valid
 */
export const validateZipCode = (zipCode: string | number | null | undefined): boolean => {
  // Handle null, undefined, or non-string values
  if (zipCode === null || zipCode === undefined) {
    return false;
  }

  // Convert to string if it's a number
  const stringZipCode = typeof zipCode === 'string' ? zipCode : String(zipCode);
  const cleanZipCode = stringZipCode.replace(/\D/g, '');

  // Check if it has exactly 8 digits
  if (cleanZipCode.length !== 8) return false;

  return true;
};

/**
 * Removes ZIP Code formatting, returning only numbers
 * @param zipCode - Formatted ZIP Code
 * @returns ZIP Code with only numbers
 */
export const cleanZipCode = (zipCode: string | number | null | undefined): string => {
  // Handle null, undefined, or non-string values
  if (zipCode === null || zipCode === undefined) {
    return '';
  }

  // Convert to string if it's a number
  const stringZipCode = typeof zipCode === 'string' ? zipCode : String(zipCode);
  return stringZipCode.replace(/\D/g, '');
};

